import { useAuth } from '@/context/auth-context';
import { filterStore } from '@/state/transactions-filter-state';
import appServices, {
  PaginatedTransactionsResponse,
} from '@/services/appServices';
import { AppTransaction, TransactionFilters } from '@polybank/shared';
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';

/**
 *
 * @param enabled whether to enable the query
 * @param customFilters filters to apply to the query that overrides the filters in the store
 * @param onlyCount this will make the query to request one record only,
 * so we can get the count of a query without transferring
 * all the data to the client
 * @returns
 */
export function useInfiniteTransactions(
  enabled = true,
  customFilters?: Partial<TransactionFilters>,
  onlyCount = false
) {
  const snap = useSnapshot(filterStore);

  const options = useMemo(() => {
    const result = {
      searchQuery:
        customFilters?.searchQuery || snap.appliedFilters.searchQuery,
      searchMode:
        customFilters?.searchMode ||
        snap.appliedFilters.searchMode ||
        'contains',
      categories:
        customFilters?.categories ||
        (snap.appliedFilters.categories
          ? [...snap.appliedFilters.categories]
          : undefined),
      accountFilter:
        customFilters?.accountFilter ||
        (snap.appliedFilters.accountFilter
          ? [...snap.appliedFilters.accountFilter]
          : undefined),
      dateRange: customFilters?.dateRange || snap.appliedFilters.dateRange,
      amountRange:
        customFilters?.amountRange || snap.appliedFilters.amountRange,
      transactionTypes:
        customFilters?.transactionTypes ||
        (snap.appliedFilters.transactionTypes
          ? [...snap.appliedFilters.transactionTypes]
          : undefined),
      merchants:
        customFilters?.merchants ||
        (snap.appliedFilters.merchants
          ? [...snap.appliedFilters.merchants]
          : undefined),
      // sortBy: snap.sortBy,
    } satisfies TransactionFilters;
    const validFilters = Object.keys(customFilters ?? {});
    if (customFilters) {
      Object.keys(result).forEach((key) => {
        if (!validFilters.includes(key as keyof TransactionFilters)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          delete (result as any)[key];
        }
      });
    }
    return result;
  }, [
    customFilters,
    snap.appliedFilters.accountFilter,
    snap.appliedFilters.amountRange,
    snap.appliedFilters.categories,
    snap.appliedFilters.dateRange,
    snap.appliedFilters.merchants,
    snap.appliedFilters.searchMode,
    snap.appliedFilters.searchQuery,
    snap.appliedFilters.transactionTypes,
  ]);

  const {
    searchQuery = '',
    searchMode,
    categories,
    accountFilter,
    dateRange,
    amountRange,
    transactionTypes,
    merchants,
  } = options;

  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();

  // Create query key that includes all filter parameters
  const queryKey = useMemo(
    () => [
      'appwrite-transactions',
      {
        searchQuery,
        searchMode,
        categories,
        accountFilter,
        dateRange: dateRange
          ? {
              from: dateRange.from?.toISOString(),
              to: dateRange.to?.toISOString(),
            }
          : undefined,
        amountRange,
        transactionTypes,
        merchants,
        onlyCount,
        sortBy: snap.sortBy,
      },
    ],
    [
      searchQuery,
      searchMode,
      categories,
      accountFilter,
      dateRange,
      amountRange,
      transactionTypes,
      merchants,
      onlyCount,
      snap.sortBy,
    ]
  );

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      // For "notContains" mode, don't pass searchQuery to backend
      // We'll filter client-side instead
      const effectiveSearchQuery =
        searchMode === 'notContains' ? '' : searchQuery;

      const result = await appServices.transactions.fetchTransactionsPaginated({
        cursor: pageParam,
        searchQuery: effectiveSearchQuery,
        categories,
        accountFilter,
        dateRange,
        amountRange,
        transactionTypes,
        merchants,
        onlyCount,
        sortBy: snap.sortBy,
      });

      // appwrite does not support notContains search mode, so we filter client-side
      if (searchMode === 'notContains' && searchQuery.trim()) {
        const lowerSearchQuery = searchQuery.toLowerCase().trim();
        const filteredTransactions = result.transactions.filter(
          (t) => !t.description?.toLowerCase().includes(lowerSearchQuery)
        );
        return {
          ...result,
          transactions: filteredTransactions,
        };
      }

      return result;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && isAuthenticated && !!user,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Flatten all pages into a single array of transactions
  const allTransactions = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.transactions) ?? [];
  }, [infiniteQuery.data]);

  const allTransactionsMap = useMemo(() => {
    return allTransactions.reduce(
      (acc, tx) => {
        acc[tx.$id!] = tx;
        return acc;
      },
      {} as Record<string, AppTransaction>
    );
  }, [allTransactions]);
  // Get total count from first page
  const totalCount = infiniteQuery.data?.pages[0]?.total ?? 0;

  const updateItem = useCallback(
    (item: Partial<AppTransaction>) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: InfiniteData<PaginatedTransactionsResponse>) => {
          return {
            ...oldData,
            pages: oldData.pages.map((page: PaginatedTransactionsResponse) => ({
              ...page,
              transactions: page.transactions.map((t: AppTransaction) =>
                t.$id === item.$id ? { ...t, ...item } : t
              ),
            })),
          };
        }
      );
    },
    [queryClient, queryKey]
  );
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);
  return {
    // Data
    transactions: allTransactions,
    totalCount,
    transactionsMap: allTransactionsMap,
    // Loading states
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isError: infiniteQuery.isError,
    error: infiniteQuery.error,
    hasFetched: infiniteQuery.isSuccess,
    // Pagination
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isPending: infiniteQuery.isPending,
    // Utilities
    refetch: infiniteQuery.refetch,
    isRefetching: infiniteQuery.isRefetching,
    updateItem,
    invalidate,
  };
}
